import millisecondsToSeconds from 'date-fns/millisecondsToSeconds';
import { useEffect, useRef, useState } from 'react';
import { FaMicrophoneSlash } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { useUnmount } from 'react-use';

import { Box, P, Waveform, WaveformLoading } from '@eluve/components';
import { formatSecondsToTime } from '@eluve/date-utils';
import { useAppointmentTotalDuration } from '@eluve/frontend-appointment-hooks';
import { useNamedLogger } from '@eluve/logger';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { useTotalAudioDurationFromLocalFiles } from '@eluve/user-local-files';

import { useTranscriptionMachineSelector } from '../hooks/useTranscriptionMachine';

interface WaveformWhenRecordingProps {
  totalDuration: number;
  onTimeDurationUpdate?: (time: number) => void;
}

const WaveformWhenRecording: React.FC<WaveformWhenRecordingProps> = ({
  totalDuration,
  onTimeDurationUpdate,
}) => {
  const clonedWaveForm = useRef<MediaStream | null>(null);

  const { mediaStream } = useTranscriptionMachineSelector((state) => {
    const { mediaStream } = state.context;
    if (
      mediaStream &&
      mediaStream.active &&
      (!clonedWaveForm.current || !clonedWaveForm.current.active)
    ) {
      clonedWaveForm.current = mediaStream.clone();
      window.eluveMediaStreams.set('waveform', clonedWaveForm.current);
    }

    return {
      mediaStream: clonedWaveForm.current,
    };
  });

  useUnmount(() => {
    if (clonedWaveForm.current) {
      clonedWaveForm.current.getTracks().forEach((track) => {
        track.stop();
      });
    }
  });

  if (!mediaStream) {
    return <WaveformLoading totalDuration={totalDuration} />;
  }

  return (
    <Waveform
      stream={mediaStream}
      totalDuration={totalDuration}
      onTimeDurationUpdate={onTimeDurationUpdate}
    />
  );
};

export const TranscriptionWaveform: React.FC = () => {
  const [recordingDuration, setRecordingDuration] = useState<number>(0);

  const isRecording = useTranscriptionMachineSelector((state) =>
    state.matches({
      InSession: 'Recording',
    }),
  );

  const isMuted = useTranscriptionMachineSelector(
    (state) => state.context.microphoneStatus === 'muted',
  );
  const logger = useNamedLogger('TranscriptionWaveform');
  const tenantId = useTenantIdFromParams() ?? '';
  const { appointmentId: appointmentIdFromParams } = useParams<{
    appointmentId?: string;
  }>();

  const appointmentId =
    useTranscriptionMachineSelector((s) => s.context.sessionAppointmentId) ??
    appointmentIdFromParams ??
    '';

  const persistedTotalDuration = useAppointmentTotalDuration(appointmentId);
  const totalDurationFromLocalFiles = useTotalAudioDurationFromLocalFiles(
    tenantId,
    appointmentId,
    logger,
  );

  useEffect(() => {
    setRecordingDuration(
      millisecondsToSeconds(
        persistedTotalDuration + totalDurationFromLocalFiles,
      ),
    );
  }, [persistedTotalDuration, totalDurationFromLocalFiles]);

  if (!isRecording) {
    return (
      <div>
        <Box hStack className="gap-6 rounded-lg p-2">
          <span className="font-mono text-sm">
            {formatSecondsToTime(recordingDuration)}
          </span>
        </Box>
      </div>
    );
  }

  if (isMuted) {
    return (
      <Box hStack className="gap-4 rounded-lg p-2 pr-4">
        <Box className="rounded-lg bg-bg-negative-subtle p-2 text-text-negative-subtle">
          <FaMicrophoneSlash className="size-5" />
        </Box>
        <P>Muted</P>
      </Box>
    );
  }

  return (
    <WaveformWhenRecording
      totalDuration={recordingDuration}
      onTimeDurationUpdate={setRecordingDuration}
    />
  );
};
